// common/ui/queue-manager.js
// Shared queue UI logic for Desktop App and Web App

import { generateDocumentId } from '../api.js';
import { settingsManager } from '../settings.js';
import { emptyResultSummary, hasResultCounts, summarizeResults } from '../result-summary.js';

export const QUEUE_HISTORY_STORAGE_KEY = 'pelaikan_desktop_queue_history';
const QUEUE_HISTORY_MAX_ITEMS = 30;

// Store File objects associated with queue rows
const rowFileMap = new WeakMap();

// Store lazy-load descriptors ({ path, name }) for rows restored from history
// whose PDF bytes have NOT been read into memory yet. Reading every recent PDF
// eagerly at startup spiked the WebView2 renderer heap (up to 30 documents held
// resident) and could re-trigger an out-of-memory crash right after a refresh.
// Bytes are now materialized on demand via ensureRowFileLoaded().
const rowLazyMap = new WeakMap();

// Store results data associated with queue rows
const rowResultsMap = new WeakMap();

// Store document IDs associated with queue rows
const rowDocumentIdMap = new WeakMap();

// Store overlay auto-dismiss timers
const rowOverlayTimerMap = new WeakMap();

// Store card overlay states ('checking' | 'ocr' | 'error'; absent = hidden)
const rowOverlayStateMap = new WeakMap();

// Store "Internet default" hint auto-dismiss timers
const rowInternetHintTimerMap = new WeakMap();

/**
 * Creates queue row helper functions
 * @param {Object} config - Configuration object
 * @param {HTMLElement} config.queueBody - The queue table body element
 * @param {HTMLElement} config.baseQueueRow - The template row to clone
 * @param {HTMLElement} config.pickedNameEl - Element to display selected file name
 * @param {HTMLInputElement} config.fileInput - File input element for syncing
 * @returns {Object} Helper functions for queue row management
 */
export function createQueueRowHelpers({ queueBody, baseQueueRow, templateRow, pickedNameEl, fileInput }) {
  let activeQueueRow = baseQueueRow || null;

  const isTauri =
    !!(window.__TAURI__ || window.__TAURI_IPC__ || window.__TAURI_METADATA__);

  const setRowText = (row, selector, text) => {
    const el = row?.querySelector(selector);
    if (el) el.textContent = text;
  };

  const setRowButtonText = (row, selector, text) => {
    const el = row?.querySelector(selector);
    if (!el) return;
    // For icon-only buttons (containing SVGs), set title instead of replacing content
    if (el.querySelector('svg')) {
      el.title = text;
    } else {
      el.textContent = text;
    }
  };

  const resetQueueRow = (row) => {
    setRowText(row, '.queue-filename-text', 'No file selected');
    setRowText(row, '.queue-folder-text', 'Internet');
    setRowText(row, '.queue-status-pill', 'Idle');
    setRowButtonText(row, '.queue-select-file', 'Choose file');
    setRowButtonText(row, '.queue-select-folder', 'Choose folder');
    const pdfReportButton = row?.querySelector('.queue-generate-pdf-report');
    if (pdfReportButton) pdfReportButton.disabled = true;
    const reportJobButton = row?.querySelector('.queue-submit-report-job');
    if (reportJobButton) reportJobButton.disabled = true;
    const toggle = row?.querySelector('.queue-inline-toggle');
    if (toggle) toggle.checked = true;
    // Clear stored file
    rowFileMap.delete(row);
    rowLazyMap.delete(row);
    // Reset results
    resetRowResults(row);
    // Clear status classes
    const pill = row?.querySelector('.queue-status-pill');
    if (pill) {
      pill.classList.remove('is-parsing', 'is-resolving', 'is-analyzing', 'is-complete', 'is-error');
    }

    // Clear and hide the "Internet default" hint
    const hintTimer = rowInternetHintTimerMap.get(row);
    if (hintTimer) clearTimeout(hintTimer);
    rowInternetHintTimerMap.delete(row);
    const hint = row?.querySelector('.queue-datasource-hint');
    if (hint) {
      hint.classList.remove('is-visible');
      hint.setAttribute('aria-hidden', 'true');
    }

    // Reset the card overlay
    if (row) hideCardOverlay(row);
  };

  /**
   * Resets the results badges to default state
   */
  const resetRowResults = (row) => {
    if (!row) return;
    rowResultsMap.delete(row);
    const badges = row.querySelectorAll('.queue-result-badge');
    badges.forEach(badge => {
      badge.textContent = '—';
      badge.classList.remove('has-count');
    });
    // Secondary labels are rendered outside the three numeric badges, so they
    // must be cleared explicitly when the document cache is removed.
    row.querySelector('.queue-flag-labels')?.replaceChildren();
  };

  /**
   * Updates the status pill with a stage and progress
   * @param {HTMLElement} row - The queue row
   * @param {string} stage - 'idle' | 'parsing' | 'resolving' | 'analyzing' | 'complete' | 'error'
   * @param {number} current - Current progress count
   * @param {number} total - Total count
   */
  const setRowProgress = (row, stage, current, total) => {
    if (!row) return;
    const pill = row.querySelector('.queue-status-pill');
    if (!pill) return;

    // Remove old status classes
    pill.classList.remove('is-parsing', 'is-resolving', 'is-analyzing', 'is-complete', 'is-error');

    let text = 'Processing';
    let cssClass = '';

    switch (stage) {
      case 'idle':
        text = 'Idle';
        break;
      case 'ready':
        text = 'Ready';
        break;
      case 'queued':
        text = 'Queued';
        break;
      case 'parse':
      case 'parsing_inline':
        text = 'Parsing inline…';
        cssClass = 'is-parsing';
        break;
      case 'parsing':
      case 'parse':
        text = total > 0 ? `Parsing ${current}/${total}` : 'Parsing…';
        cssClass = 'is-parsing';
        break;
      case 'resolve':
      case 'resolving':
        text = total > 0 ? `Resolving ${current}/${total}` : 'Resolving…';
        cssClass = 'is-resolving';
        break;
      case 'analyze':
      case 'analyzing':
        text = total > 0 ? `Analyzing ${current}/${total}` : 'Analyzing…';
        cssClass = 'is-analyzing';
        break;
      case 'complete':
        text = 'Complete';
        cssClass = 'is-complete';
        break;
      case 'error':
        text = 'Error';
        cssClass = 'is-error';
        break;
      default:
        text = 'Processing';
    }

    pill.textContent = text;
    if (cssClass) {
      pill.classList.add(cssClass);
    }
    // Toggle processing state on the card for badge animations
    const isProcessing = ['is-parsing', 'is-resolving', 'is-analyzing'].includes(cssClass);
    row.classList.toggle('is-processing', isProcessing);
  };

  /**
   * Updates the results badges with verification counts
   * @param {HTMLElement} row - The queue row
   * @param {Object} results - { verified: number, issues: number, unresolved: number }
   */
  const setRowResults = (row, results) => {
    if (!row || !results) return;
    rowResultsMap.set(row, results);

    const verifiedBadge = row.querySelector('.queue-result-badge.verified');
    const issuesBadge = row.querySelector('.queue-result-badge.issues');
    const unresolvedBadge = row.querySelector('.queue-result-badge.unresolved');

    if (verifiedBadge) {
      const count = results.verified || 0;
      verifiedBadge.textContent = count > 0 ? String(count) : '—';
      verifiedBadge.classList.toggle('has-count', count > 0);
    }
    if (issuesBadge) {
      const count = results.issues || 0;
      issuesBadge.textContent = count > 0 ? String(count) : '—';
      issuesBadge.classList.toggle('has-count', count > 0);
    }
    if (unresolvedBadge) {
      const count = results.unresolved || 0;
      unresolvedBadge.textContent = count > 0 ? String(count) : '—';
      unresolvedBadge.classList.toggle('has-count', count > 0);
    }

    // Render flag labels (hallucinations, quote mismatches)
    const flagsContainer = row.querySelector('.queue-flag-labels');
    if (flagsContainer) {
      flagsContainer.innerHTML = '';
      if (results.hallucinations > 0) {
        const tag = document.createElement('span');
        tag.className = 'queue-flag-label hallucination';
        tag.textContent = `Hallucination${results.hallucinations > 1 ? 's' : ''} (${results.hallucinations})`;
        flagsContainer.appendChild(tag);
      }
      if (results.quoteMismatches > 0) {
        const tag = document.createElement('span');
        tag.className = 'queue-flag-label quote-mismatch';
        tag.textContent = `Quote mismatch${results.quoteMismatches > 1 ? 'es' : ''} (${results.quoteMismatches})`;
        flagsContainer.appendChild(tag);
      }
    }
  };

  /**
   * Gets stored results for a row
   */
  const getRowResults = (row) => rowResultsMap.get(row);

  const cloneQueueRow = () => {
    if (!queueBody) return null;
    const source = baseQueueRow || templateRow;
    if (!source) return null;
    const row = source.cloneNode(true);
    row.classList.remove('is-active');
    row.querySelector('#btn-select-pdf')?.removeAttribute('id');
    resetQueueRow(row);
    return row;
  };

  const isRowEmpty = (row) => {
    const filename = row?.querySelector('.queue-filename-text')?.textContent || '';
    return filename.trim().toLowerCase() === 'no file selected';
  };

  // Sync file input with a row's stored file
  const syncFileInputToRow = (row) => {
    if (!row || !fileInput) return;
    const file = rowFileMap.get(row);
    if (file) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInput.files = dataTransfer.files;
      if (pickedNameEl) pickedNameEl.textContent = file.name;
    }
  };

  const setActiveQueueRow = (row) => {
    if (!queueBody) return;
    activeQueueRow = row || null;
    queueBody.querySelectorAll('.queue-row').forEach((item) => {
      item.classList.toggle('is-active', item === row);
    });
    if (row) {
      // Sync file input to the newly active row
      syncFileInputToRow(row);
    }
  };

  const getActiveQueueRow = () => activeQueueRow;

  // Get the File object stored for a row
  const getRowFile = (row) => rowFileMap.get(row);

  // --- Card overlay state machine ------------------------------------------
  // The card overlay mirrors real document-preparation state instead of fixed
  // timers:
  //   hidden   – nothing to report (document already prepared, or idle)
  //   checking – file just landed; looking up cached OCR / prior results
  //   ocr      – a parse (OCR) is actually in flight for this row's document
  //   error    – the parse failed; auto-hides after a short delay
  // Desktop and Web share this implementation. The "Pick local folder" action
  // is rendered only when the row's template has a data-source chip (Desktop).
  const OVERLAY_FAILSAFE_MS = {
    checking: 20000,  // cache lookup should resolve quickly; bail out if not
    ocr: 600000,      // OCR of long documents can take minutes
    error: 6000,
  };

  const getCardOverlayState = (row) => (row && rowOverlayStateMap.get(row)) || 'hidden';

  const rowHasFolderChip = (row) =>
    !!row?.querySelector?.('.queue-card-meta-row .queue-select-folder');

  const setCardOverlay = (row, state, opts = {}) => {
    if (!row) return;
    const overlay = row.querySelector('.queue-card-overlay');
    if (!overlay) return;

    const prevTimer = rowOverlayTimerMap.get(row);
    if (prevTimer) clearTimeout(prevTimer);
    rowOverlayTimerMap.delete(row);

    if (state === 'hidden') {
      rowOverlayStateMap.delete(row);
      overlay.classList.add('is-fading');
      overlay.classList.remove('is-visible', 'is-checking');
      overlay.setAttribute('aria-hidden', 'true');
      return;
    }
    rowOverlayStateMap.set(row, state);

    const folderText = row.querySelector('.queue-folder-text')?.textContent?.trim() || 'Internet';
    const showFolderAction = state === 'ocr' && rowHasFolderChip(row) && folderText === 'Internet';

    const defaults = {
      checking: { title: 'Checking document…' },
      ocr: {
        title: 'Performing OCR…',
        detail: showFolderAction
          ? 'You can pick a local data source while OCR continues.'
          : 'Extracting pages, headings, and citations. This can take a minute for longer PDFs.',
      },
      error: { title: 'Document preparation failed' },
    }[state] || {};

    overlay.innerHTML = `
      <div class="queue-overlay-content">
        ${state === 'error' ? '' : '<div class="queue-overlay-spinner" aria-hidden="true"></div>'}
        <span class="queue-overlay-title"></span>
        <span class="queue-overlay-detail"></span>
        ${showFolderAction
          ? '<div class="queue-overlay-options"><button class="queue-overlay-btn queue-overlay-folder queue-select-folder" type="button" title="Use a local folder of PDFs as your reference library">Pick local folder</button></div>'
          : ''}
      </div>`;
    // Text goes through textContent so caller-supplied strings (filenames,
    // error messages) cannot inject markup.
    overlay.querySelector('.queue-overlay-title').textContent = opts.title || defaults.title || '';
    const detailEl = overlay.querySelector('.queue-overlay-detail');
    const detailText = opts.detail !== undefined ? opts.detail : (defaults.detail || '');
    detailEl.textContent = detailText;
    detailEl.style.display = detailText ? '' : 'none';

    overlay.classList.remove('is-fading');
    overlay.classList.toggle('is-checking', state === 'checking');
    overlay.classList.add('is-visible');
    overlay.setAttribute('aria-hidden', 'false');

    const failsafe = OVERLAY_FAILSAFE_MS[state];
    if (failsafe) {
      rowOverlayTimerMap.set(row, setTimeout(() => setCardOverlay(row, 'hidden'), failsafe));
    }
  };

  // Update text of a visible overlay without changing its state
  const updateCardOverlay = (row, { title, detail } = {}) => {
    if (!row || getCardOverlayState(row) === 'hidden') return;
    const overlay = row.querySelector('.queue-card-overlay');
    if (!overlay) return;
    if (title) {
      const el = overlay.querySelector('.queue-overlay-title');
      if (el) el.textContent = title;
    }
    if (detail !== undefined) {
      const el = overlay.querySelector('.queue-overlay-detail');
      if (el) {
        el.textContent = detail;
        el.style.display = detail ? '' : 'none';
      }
    }
  };

  // Replace the overlay's action button with a confirmation (e.g. after the
  // user picked a local folder while OCR keeps running)
  const confirmCardOverlayAction = (row, label) => {
    const optionsEl = row?.querySelector?.('.queue-card-overlay .queue-overlay-options');
    if (!optionsEl) return;
    optionsEl.innerHTML = '';
    const confirmed = document.createElement('span');
    confirmed.className = 'queue-overlay-confirmed';
    confirmed.textContent = label;
    optionsEl.appendChild(confirmed);
  };

  const hideCardOverlay = (row) => setCardOverlay(row, 'hidden');

  const showInternetDefaultHint = (row, maxMs = 4500) => {
    if (!isTauri || !row) return;
    try {
      if (sessionStorage.getItem('fc:internet-default-hint-shown') === '1') return;
      sessionStorage.setItem('fc:internet-default-hint-shown', '1');
    } catch {
      // sessionStorage may be unavailable in some contexts; fail open (show hint).
    }
    const hint = row.querySelector('.queue-datasource-note');
    if (!hint) return;

    const prev = rowInternetHintTimerMap.get(row);
    if (prev) clearTimeout(prev);

    row.classList.add('show-ds-note');
    hint.classList.add('is-visible');
    hint.setAttribute('aria-hidden', 'false');

    const timer = setTimeout(() => {
      hint.classList.remove('is-visible');
      hint.setAttribute('aria-hidden', 'true');
      row.classList.remove('show-ds-note');
      rowInternetHintTimerMap.delete(row);
    }, maxMs);
    rowInternetHintTimerMap.set(row, timer);
  };

  const hideInternetDefaultHint = (row) => {
    if (!row) return;
    const prev = rowInternetHintTimerMap.get(row);
    if (prev) clearTimeout(prev);
    rowInternetHintTimerMap.delete(row);
    row.classList.remove('show-ds-note');
    const hint = row.querySelector('.queue-datasource-note');
    if (hint) {
      hint.classList.remove('is-visible');
      hint.setAttribute('aria-hidden', 'true');
    }
  };

  const setQueueRowFile = (row, file) => {
    if (!row || !file) return;
    // Store the actual File object
    rowFileMap.set(row, file);
    setRowText(row, '.queue-filename-text', file.name);
    setRowText(row, '.queue-status-pill', 'Ready');
    setRowButtonText(row, '.queue-select-file', 'Change file');
    const pdfReportButton = row.querySelector('.queue-generate-pdf-report');
    if (pdfReportButton) pdfReportButton.disabled = false;
    const reportJobButton = row.querySelector('.queue-submit-report-job');
    if (reportJobButton) reportJobButton.disabled = false;
    if (pickedNameEl) pickedNameEl.textContent = file.name;
    // Overlay state is driven by loadCachedResultsForFile and fc:parse-* events

    const sourceText = row.querySelector('.queue-folder-text')?.textContent || '';
    if (sourceText.trim().toLowerCase() === 'internet') {
      showInternetDefaultHint(row);
    } else {
      hideInternetDefaultHint(row);
    }
  };

  const setQueueRowFolder = (row, folderName) => {
    if (!row || !folderName) return;
    setRowText(row, '.queue-folder-text', folderName);
    setRowButtonText(row, '.queue-select-folder', 'Change folder');
    hideInternetDefaultHint(row);
    // If the OCR overlay offered the "Pick local folder" action, confirm it
    // in place instead of dismissing the overlay while OCR continues.
    if (getCardOverlayState(row) === 'ocr' && folderName.trim().toLowerCase() !== 'internet') {
      confirmCardOverlayAction(row, `Local folder: ${folderName} ✓`);
      updateCardOverlay(row, { detail: 'OCR continues with your local folder as the data source.' });
    }
  };

  // Get/set document ID for a row
  const getRowDocumentId = (row) => rowDocumentIdMap.get(row);
  const setRowDocumentId = (row, documentId) => {
    if (!row || !documentId) return;
    const changed = rowDocumentIdMap.get(row) !== documentId;
    rowDocumentIdMap.set(row, documentId);
    // A card only becomes groupable once it has an identity; announce it so
    // features that decorate cards (grouping) do not have to poll.
    if (changed) {
      window.dispatchEvent(new CustomEvent('fc:queue-row-document', { detail: { row, documentId } }));
    }
  };

  return {
    setRowText,
    setRowButtonText,
    resetQueueRow,
    resetRowResults,
    cloneQueueRow,
    isRowEmpty,
    setActiveQueueRow,
    getActiveQueueRow,
    getRowFile,
    setQueueRowFile,
    setQueueRowFolder,
    setRowProgress,
    setRowResults,
    getRowResults,
    getRowDocumentId,
    setRowDocumentId,
    syncFileInputToRow,
    setCardOverlay,
    getCardOverlayState,
    updateCardOverlay,
    confirmCardOverlayAction,
    hideCardOverlay
  };
}

/**
 * Updates a file input element with a file (triggers change event)
 * @param {HTMLInputElement} fileInput - The file input element
 * @param {File} file - The file to set
 */
export function updateFileInput(fileInput, file) {
  if (!fileInput || !file) return;
  fileInput.dataset.fcQueueSync = '1';

  // IMPORTANT: Store the file path in a data attribute before setting via DataTransfer
  // DataTransfer creates a new File object that loses custom properties like __fcPath
  const filePath = file.path || file.__fcPath || '';
  if (filePath) {
    fileInput.dataset.fcFilePath = filePath;
    console.log('[Queue] Stored file path in data attribute:', filePath);
  } else {
    delete fileInput.dataset.fcFilePath;
    console.log('[Queue] No file path to store for:', file.name);
  }

  const dataTransfer = new DataTransfer();
  dataTransfer.items.add(file);
  fileInput.files = dataTransfer.files;
  fileInput.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Sets up all queue-related event listeners
 * @param {Object} config - Configuration object
 * @param {HTMLElement} config.queueBody - The queue table body element
 * @param {HTMLElement} config.queueDropzone - The dropzone element
 * @param {HTMLElement} config.btnAddRow - Add row button
 * @param {HTMLElement} config.btnQueueGo - Go/run button
 * @param {HTMLInputElement} config.fileInput - File input element
 * @param {HTMLInputElement} config.folderInput - Folder input element
 * @param {Object} config.helpers - Queue row helper functions from createQueueRowHelpers
 * @param {AbortSignal} config.signal - AbortController signal for cleanup
 * @param {Object} config.platform - Platform adapter with setStatus method
 * @param {HTMLElement} config.btnParse - Parse PDF button (optional)
 * @param {HTMLElement} config.btnParseInline - Parse inline button (optional)
 * @param {HTMLElement} config.btnVerifyAll - Verify all / Audit all button
 * @param {HTMLElement} config.btnGeneratePdfReport - Generate PDF report button (optional)
 * @param {HTMLElement} config.btnQueueVerifyAll - Verify all documents button (optional)
 * @param {Function} config.getMgr - Function returning the FootnotesManager instance
 * @param {Function} config.onShowSingleView - Callback to open single-file view
 * @param {Function} config.onSelectFile - Optional file picker handler for queue rows
 * @param {Function} config.onSelectFolder - Optional folder picker handler for queue rows
 * @param {Function} config.onFolderSelected - Optional hook after folder input selection
 * @param {Function} config.onQueueFileSelected - Optional hook after a file is assigned to a row
 * @param {Function} config.onSubmitReportJob - Optional async report-job submit handler
 * @param {string} config.apiBase - API base URL for fetching cached results
 * @param {Function} config.getAccessToken - Function to get access token for API calls
 * @param {Function} config.getClientId - Optional function to get client ID for bridge mode (Desktop App)
 * @param {Function} config.getDocumentPath - Optional function to get document path (Desktop App)
 * @param {Function} config.getDocumentId - Optional function to get document ID (Desktop App)
 */
export function setupQueueEventListeners({
  queueBody,
  queueDropzone,
  btnAddRow,
  btnQueueGo,
  fileInput,
  folderInput,
  helpers,
  signal,
  platform,
  btnParse,
  btnParseInline,
  btnVerifyAll,
  btnGeneratePdfReport,
  btnQueueVerifyAll,
  onShowSingleView,
  onSelectFile,
  onSelectFolder,
  onFolderSelected,
  onQueueFileSelected,
  onSubmitReportJob,
  getMgr,
  apiBase,
  getAccessToken,
  getClientId,
  getDocumentPath,
  getDocumentId
}) {
  const {
    setActiveQueueRow,
    getActiveQueueRow,
    resetQueueRow,
    cloneQueueRow,
    isRowEmpty,
    getRowFile,
    setQueueRowFile,
    setQueueRowFolder,
    setRowText,
    setRowButtonText,
    setRowProgress,
    setRowResults,
    getRowDocumentId,
    setRowDocumentId,
    syncFileInputToRow,
    setCardOverlay,
    getCardOverlayState,
    updateCardOverlay,
    hideCardOverlay
  } = helpers;

  // True when a row has a usable document — either its File bytes are already in
  // memory, or it was restored from history and can be lazily materialized. UI
  // that only cares about presence (button enablement, "verify all" selection)
  // should use this so history-restored rows are not treated as empty.
  const rowHasFile = (row) => rowFileMap.has(row) || rowLazyMap.has(row);

  // Materialize a row's File on demand. Rows restored from history hold only a
  // { path, name } descriptor until the user actually acts on them, at which
  // point we read the PDF bytes from disk exactly once. Side-effect-free beyond
  // caching the File (no pill/overlay changes), so callers keep full control of
  // presentation. Returns the File (real, with bytes) or null if unavailable.
  const ensureRowFileLoaded = async (row) => {
    if (!row) return null;
    const existing = rowFileMap.get(row);
    if (existing) return existing;
    const lazy = rowLazyMap.get(row);
    if (!lazy?.path) return null;
    try {
      const [file] = await createFilesFromTauriPaths([lazy.path]);
      if (file) {
        rowFileMap.set(row, file);
        rowLazyMap.delete(row);
        return file;
      }
    } catch (error) {
      console.warn('[Queue] Failed to lazy-load file for row:', lazy.path, error);
    }
    return null;
  };

  // Loads the row's File for an action, reporting failure honestly: a lazy
  // history row whose backing file can no longer be read (moved, renamed,
  // deleted, or on an unavailable network drive) is different from a row
  // with no file at all, and telling the user to "add a PDF" for a row that
  // visibly has one is misleading. The lazy descriptor is kept so the user
  // can restore the file location and retry, or re-attach via "Change file".
  const loadRowFileForAction = async (row, emptyMessage) => {
    if (!row) {
      if (emptyMessage) platform.setStatus?.(emptyMessage);
      return null;
    }
    const file = await ensureRowFileLoaded(row);
    if (file) return file;
    if (rowHasFile(row)) {
      const name = rowLazyMap.get(row)?.name ||
        row.querySelector('.queue-filename-text')?.textContent?.trim() ||
        'this document';
      platform.setStatus?.(`Could not read "${name}" from disk — the file may have been moved or renamed. Use "Change file" to re-attach it.`);
    } else if (emptyMessage) {
      platform.setStatus?.(emptyMessage);
    }
    return null;
  };

  const queuePanel = queueBody?.closest('.queue-panel');
  const getRows = () => Array.from(queueBody?.querySelectorAll('.queue-row') || []);
  const getRowsWithFiles = () => getRows().filter((row) => rowHasFile(row));
  const isTauriContext = () => {
    try {
      return !!(window.__TAURI__ || window.__TAURI_IPC__ || window.__TAURI_METADATA__);
    } catch {
      return false;
    }
  };

  const setDropzoneDragState = (isActive) => {
    queueDropzone?.classList.toggle('is-dragover', isActive);
    queuePanel?.classList.toggle('is-dragover', isActive);
  };

  const getPdfFilesFromDataTransfer = (dataTransfer) => {
    const files = Array.from(dataTransfer?.files || []);
    if (files.length) {
      return files.filter((file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
    }

    return Array.from(dataTransfer?.items || [])
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile?.())
      .filter((file) => file && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')));
  };

  const getBasename = async (path) => {
    try {
      const { basename } = await import('@tauri-apps/api/path');
      return await basename(path);
    } catch {
      return String(path || '').split(/[\\/]/).pop() || 'document.pdf';
    }
  };

  const createFilesFromTauriPaths = async (paths = []) => {
    const pdfPaths = (paths || []).filter((path) => String(path || '').toLowerCase().endsWith('.pdf'));
    if (!pdfPaths.length) return [];

    const { readFile } = await import('@tauri-apps/plugin-fs');
    const files = [];
    for (const path of pdfPaths) {
      const bytes = await readFile(path);
      const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
      const name = await getBasename(path);
      const file = new File([data], name, { type: 'application/pdf' });
      file.__fcPath = path;
      files.push(file);
    }
    return files;
  };

  const historyEnabled = () => settingsManager?.isHistoryEnabled?.() !== false;

  const normalizeHistoryPath = (path) => String(path || '').trim().toLowerCase();
  const cacheRequestKey = (documentId, path) => JSON.stringify([
    String(documentId || ''), normalizeHistoryPath(path)
  ]);

  const historyIdentityKey = (item) => item?.documentId
    ? `document:${item.documentId}`
    : `path:${normalizeHistoryPath(item?.path)}`;

  const dedupeHistoryItems = (items) => {
    const seen = new Set();
    return (items || [])
      .filter((item) => item?.path)
      .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
      .filter((item) => {
        const key = historyIdentityKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const readSavedQueueHistory = () => {
    if (!historyEnabled()) return [];
    try {
      const parsed = JSON.parse(localStorage.getItem(QUEUE_HISTORY_STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      const deduped = dedupeHistoryItems(parsed);
      if (deduped.length !== parsed.length) {
        localStorage.setItem(QUEUE_HISTORY_STORAGE_KEY, JSON.stringify(deduped));
      }
      return deduped;
    } catch (error) {
      console.warn('[Queue] Failed to read queue history:', error);
      return [];
    }
  };

  const getFilePathForHistory = (file) => file?.path || file?.__fcPath || '';

  const getRowDocumentPath = (row) => {
    const file = getRowFile(row);
    const lazy = rowLazyMap.get(row);
    return getFilePathForHistory(file) || lazy?.path || getDocumentPath?.() || '';
  };

  const getRowHistorySnapshot = (row) => {
    const file = getRowFile(row);
    const lazy = rowLazyMap.get(row);
    // A row may be a fully-loaded File or a not-yet-materialized history entry;
    // either must be persisted so restored documents survive the next save.
    const path = getFilePathForHistory(file) || lazy?.path || '';
    if (!row || !path) return null;
    const name = file?.name || lazy?.name || String(path).split(/[\\/]/).pop() || 'document.pdf';

    const inlineToggle = row.querySelector('.queue-inline-toggle');
    const folderName = row.querySelector('.queue-folder-text')?.textContent?.trim() || 'Internet';
    return {
      path,
      name,
      documentId: getRowDocumentId(row) || '',
      folderName,
      inline: inlineToggle ? !!inlineToggle.checked : true,
      open: true,
      updatedAt: new Date().toISOString()
    };
  };

  const writeQueueHistory = () => {
    try {
      if (!historyEnabled()) {
        localStorage.removeItem(QUEUE_HISTORY_STORAGE_KEY);
        return;
      }

      const snapshots = getRows()
        .map(getRowHistorySnapshot)
        .filter(Boolean);
      const previous = readSavedQueueHistory()
        .map(({ results: _discardedResults, ...item }) => ({ ...item, open: false }))
        .filter((item) => item?.path);
      const saved = dedupeHistoryItems([...previous, ...snapshots])
        .slice(0, QUEUE_HISTORY_MAX_ITEMS);
      localStorage.setItem(QUEUE_HISTORY_STORAGE_KEY, JSON.stringify(saved));
      window.dispatchEvent(new CustomEvent('fc:queue-history-updated', { detail: { history: saved } }));
    } catch (error) {
      console.warn('[Queue] Failed to save queue history:', error);
    }
  };

  let queueHistorySaveTimer = null;
  let isRestoringQueueHistory = false;
  const scheduleQueueHistorySave = () => {
    if (isRestoringQueueHistory) return;
    if (queueHistorySaveTimer) clearTimeout(queueHistorySaveTimer);
    queueHistorySaveTimer = setTimeout(() => {
      queueHistorySaveTimer = null;
      writeQueueHistory();
    }, 250);
  };

  const restoreSavedQueueHistory = async () => {
    if (!queueBody || !isTauriContext()) return;
    const saved = readSavedQueueHistory().filter((item) => item?.open !== false);
    if (!saved.length) return;

    console.log('[Queue] Restoring queue history:', saved.length, 'items');
    let firstRow = null;
    const rowsToRefresh = [];
    isRestoringQueueHistory = true;

    try {
      for (const item of saved) {
        if (!item?.path) continue;
        try {
          // Restore file identity from stored metadata ONLY — do not read the
          // PDF bytes here. Result counts in localStorage are merely a
          // last-viewed snapshot; the document database is authoritative and
          // is refreshed below. Showing the snapshot before the bridge answers
          // creates stale counts after the adjacent data folder is deleted.
          // Bytes are
          // read lazily (ensureRowFileLoaded) the first time the user acts on
          // the row. This keeps the startup heap flat regardless of how many
          // documents are in history, and avoids a crash → refresh → reload-all
          // → out-of-memory loop.
          const row = cloneQueueRow();
          if (!row) continue;
          queueBody.appendChild(row);

          const name = item.name || String(item.path).split(/[\\/]/).pop() || 'document.pdf';
          rowLazyMap.set(row, { path: item.path, name });
          // Mirror the "ready" presentation that setQueueRowFile() gives, minus
          // the File itself.
          setRowText(row, '.queue-filename-text', name);
          setRowText(row, '.queue-status-pill', 'Ready');
          setRowButtonText(row, '.queue-select-file', 'Change file');

          if (item.documentId) setRowDocumentId(row, item.documentId);
          if (item.folderName) setQueueRowFolder(row, item.folderName);

          const inlineToggle = row.querySelector('.queue-inline-toggle');
          if (inlineToggle) inlineToggle.checked = item.inline !== false;

          helpers.resetRowResults?.(row);
          setRowProgress(row, 'ready', 0, 0);

          if (item.documentId) {
            rowsToRefresh.push(row);
          }

          if (!firstRow) firstRow = row;
        } catch (error) {
          console.warn('[Queue] Could not restore history item:', item.path, error);
        }
      }
    } finally {
      isRestoringQueueHistory = false;
    }

    if (rowsToRefresh.length) void updateRowsResultsFromCache(rowsToRefresh);

    if (firstRow) {
      queueBody.querySelectorAll('.queue-row').forEach((row) => {
        if (row !== firstRow && isRowEmpty(row)) row.remove();
      });
      setActiveQueueRow(firstRow);
      updateQueueState();
      scheduleQueueHistorySave();
      platform.setStatus?.('Restored recent documents.');
    }
  };

  const restoreHistoryItemToQueue = (item) => {
    if (!queueBody || !item?.path) return null;
    const key = normalizeHistoryPath(item.path);
    const existing = getRows().find((row) => {
      const file = getRowFile(row);
      const lazy = rowLazyMap.get(row);
      return normalizeHistoryPath(getFilePathForHistory(file) || lazy?.path) === key;
    });
    if (existing) {
      setActiveQueueRow(existing);
      updateQueueState();
      return existing;
    }

    const row = cloneQueueRow();
    if (!row) return null;
    queueBody.appendChild(row);

    const name = item.name || String(item.path).split(/[\\/]/).pop() || 'document.pdf';
    rowLazyMap.set(row, { path: item.path, name });
    setRowText(row, '.queue-filename-text', name);
    setRowText(row, '.queue-status-pill', 'Ready');
    setRowButtonText(row, '.queue-select-file', 'Change file');
    if (item.documentId) setRowDocumentId(row, item.documentId);
    if (item.folderName) setQueueRowFolder(row, item.folderName);

    const inlineToggle = row.querySelector('.queue-inline-toggle');
    if (inlineToggle) inlineToggle.checked = item.inline !== false;

    helpers.resetRowResults?.(row);
    setRowProgress(row, 'ready', 0, 0);

    if (item.documentId) {
      void updateRowResultsFromCache(row);
    }

    setActiveQueueRow(row);
    updateQueueState();
    return row;
  };

  signal?.addEventListener('abort', () => {
    if (queueHistorySaveTimer) clearTimeout(queueHistorySaveTimer);
  }, { once: true });
  const handleSettingsChanged = () => writeQueueHistory();
  settingsManager?.addListener?.(handleSettingsChanged);
  signal?.addEventListener('abort', () => {
    settingsManager?.removeListener?.(handleSettingsChanged);
  }, { once: true });

  window.addEventListener('fc:queue-open-history-document', (event) => {
    const row = restoreHistoryItemToQueue(event?.detail?.item || {});
    if (row) {
      platform.setStatus?.('Document added to the audit queue.');
    }
  }, { signal });
  let detailsLockCount = 0;

  const updateDetailsLockState = () => {
    const locked = detailsLockCount > 0;
    getRows().forEach((row) => {
      const lockableButtons = [
        row.querySelector('.queue-show-single'),
        row.querySelector('.queue-generate-pdf-report'),
        row.querySelector('.queue-submit-report-job'),
      ].filter(Boolean);
      lockableButtons.forEach((button) => {
        const hasFile = rowHasFile(row);
        const needsFile = button.classList.contains('queue-generate-pdf-report')
          || button.classList.contains('queue-submit-report-job');
        button.disabled = locked || (!hasFile && needsFile);
        button.classList.toggle('is-disabled', locked);
        button.title = locked
          ? 'This action is available after the current queue check finishes.'
          : '';
      });
      // While a check runs, also lock the controls that would mutate the
      // queue or start a second run: the queue shares one file input and one
      // FootnotesManager, so a concurrent run (or a file/folder change) would
      // interleave two documents through the same state. Titles are left
      // alone — these are icon buttons with their own tooltips.
      row.querySelectorAll('.queue-check-row, .queue-select-file, .queue-select-folder, .queue-remove-row')
        .forEach((button) => {
          button.disabled = locked;
          button.classList.toggle('is-disabled', locked);
        });
    });
    if (btnQueueGo) btnQueueGo.disabled = locked;
    if (btnQueueVerifyAll) btnQueueVerifyAll.disabled = locked;
  };

  const lockDetails = () => {
    detailsLockCount += 1;
    updateDetailsLockState();
  };

  const unlockDetails = () => {
    detailsLockCount = Math.max(0, detailsLockCount - 1);
    updateDetailsLockState();
  };

  const updateQueueState = () => {
    const rows = getRows();
    const hasRows = rows.length > 0;
    queuePanel?.classList.toggle('is-empty', !hasRows);
    if (btnQueueVerifyAll) {
      const showBulk = getRowsWithFiles().length >= 2;
      btnQueueVerifyAll.classList.toggle('hidden', !showBulk);
      queuePanel?.classList.toggle('has-bulk-action', showBulk);
    }
    updateDetailsLockState();
    scheduleQueueHistorySave();
  };

  const runActiveDocumentVerification = async () => {
    const mgr = getMgr?.();
    if (!mgr?.verifyAllSmart) {
      throw new Error('Verification manager is not ready.');
    }

    if (btnVerifyAll) btnVerifyAll.disabled = true;
    try {
      // Re-running a completed document performs a fresh congruence analysis
      // while retaining a successful source resolution. Incomplete documents
      // continue from their missing work.
      await mgr.verifyAllSmart({ recheckIfComplete: true });
    } finally {
      if (btnVerifyAll) btnVerifyAll.disabled = false;
    }
  };

  const generateQueuePdfReport = async (row) => {
    if (detailsLockCount > 0) {
      platform.setStatus?.('PDF reports are available after the current queue check finishes.');
      return;
    }

    const file = await loadRowFileForAction(row, 'Choose a PDF before generating a report.');
    if (!file) return;
    if (!btnGeneratePdfReport) {
      platform.setStatus?.('PDF report generation is not available in this view.');
      return;
    }

    const reportButton = row.querySelector('.queue-generate-pdf-report');
    const originalText = reportButton?.textContent;
    try {
      reportButton && (reportButton.disabled = true);
      if (reportButton) reportButton.textContent = 'Preparing...';
      platform.setStatus?.(`Preparing PDF report for ${file.name}...`);

      setActiveQueueRow(row);
      updateFileInput(fileInput, file);
      const reportDocId = getRowDocumentId(row) || null;
      const reportContext = await waitForDocumentContext(file, {
        timeoutMs: 120000,
        expectedDocumentId: reportDocId
      });
      if (!reportContext) {
        platform.setStatus?.(`Could not load ${file.name}, so the report could not be generated.`);
        return;
      }
      const parsed = await waitForPdfParsing(120000, {
        expectedDocumentId: reportContext.documentId || reportDocId
      });
      if (!parsed) {
        platform.setStatus?.('PDF parsing did not finish, so the report could not be generated.');
        return;
      }

      if (btnGeneratePdfReport.disabled) {
        const mgr = getMgr?.();
        if (!mgr?.canGeneratePdfReport?.()) {
          platform.setStatus?.('Original PDF bytes are not available for annotation. Please re-open the file.');
          return;
        }
        btnGeneratePdfReport.disabled = false;
      }
      btnGeneratePdfReport.click();
    } finally {
      if (reportButton) {
        reportButton.textContent = originalText || 'PDF Report';
        reportButton.disabled = !rowHasFile(row);
      }
    }
  };

  const submitQueueReportJob = async (row) => {
    if (detailsLockCount > 0) {
      platform.setStatus?.('Email report jobs are available after the current queue check finishes.');
      return;
    }
    const file = await loadRowFileForAction(row, 'Choose a PDF before requesting an email report.');
    if (!file) return;
    if (!onSubmitReportJob) {
      platform.setStatus?.('Email report jobs are not available in this view.');
      return;
    }

    const button = row.querySelector('.queue-submit-report-job');
    const originalText = button?.textContent;
    try {
      if (button) {
        button.disabled = true;
        button.textContent = 'Queued...';
      }
      setActiveQueueRow(row);
      await onSubmitReportJob({ row, file });
      setRowProgress(row, 'queued', 0, 0);
    } catch (error) {
      console.error('[Queue] Failed to submit report job:', error);
      platform.setStatus?.(`Could not queue email report: ${error.message || error}`);
    } finally {
      if (button) {
        button.textContent = originalText || 'Email report';
        button.disabled = !rowHasFile(row);
      }
    }
  };

  // Resolves with the fc:document-context-updated detail once the app's
  // file-change handler has swapped the manager over to this row's document,
  // or null on timeout. Callers MUST treat null as failure: until the event
  // fires, the manager still holds the previous document, and acting on that
  // state attributes the previous document's pages/results to this row.
  const waitForDocumentContext = (file, { timeoutMs = 5000, expectedDocumentId = null } = {}) => new Promise((resolve) => {
    const matches = (detail) => {
      // Prefer the strongest identity available: the document ID hashed from
      // the row's file bytes. File names alone can collide across rows.
      if (expectedDocumentId && detail.documentId) {
        return String(detail.documentId) === String(expectedDocumentId);
      }
      return !file || !detail.fileName || detail.fileName === file.name;
    };
    let timer = null;
    const handler = (event) => {
      const detail = event?.detail || {};
      if (matches(detail)) {
        clearTimeout(timer);
        window.removeEventListener('fc:document-context-updated', handler);
        resolve(detail);
      }
    };
    window.addEventListener('fc:document-context-updated', handler);
    timer = setTimeout(() => {
      window.removeEventListener('fc:document-context-updated', handler);
      resolve(null);
    }, timeoutMs);
  });

  const addFilesToQueue = async (files, source = 'drop') => {
    // Adding a file re-points the shared document context (managers, SSE) at
    // the new document, which would corrupt a verification in progress.
    if (detailsLockCount > 0) {
      platform.setStatus?.('Adding files is available after the current queue check finishes.');
      return;
    }
    const pdfFiles = (files || []).filter((file) => {
      return file?.type === 'application/pdf' || file?.name?.toLowerCase().endsWith('.pdf');
    });
    if (!pdfFiles.length) {
      platform.setStatus?.('Drop one or more PDF files.');
      return;
    }

    console.log(`[Queue] ${source}: received`, pdfFiles.length, 'PDF files');

    let firstRow = null;
    let addedRows = false;
    for (const [index, file] of pdfFiles.entries()) {
      console.log(`[Queue] ${source} file ${index + 1}:`, {
        name: file.name,
        'file.path': file.path || '(not available)',
        'file.__fcPath': file.__fcPath || '(not set)'
      });

      if (file?.path && !file.__fcPath) {
        file.__fcPath = file.path;
      }

      const newRow = cloneQueueRow();
      if (newRow && queueBody) {
        queueBody.appendChild(newRow);
        setQueueRowFile(newRow, file);
        loadCachedResultsForFile(newRow, file);
        if (onQueueFileSelected) {
          await onQueueFileSelected({ row: newRow, file, isQueueAdd: true, isQueueSync: false });
        }
        if (!firstRow) firstRow = newRow;
        addedRows = true;
        continue;
      }

      const fallbackRow = getActiveQueueRow();
      if (fallbackRow) {
        setQueueRowFile(fallbackRow, file);
        loadCachedResultsForFile(fallbackRow, file);
        if (onQueueFileSelected) {
          await onQueueFileSelected({ row: fallbackRow, file, isQueueAdd: true, isQueueSync: false });
        }
        if (!firstRow) firstRow = fallbackRow;
      }
    }

    if (queueBody && addedRows) {
      queueBody.querySelectorAll('.queue-row').forEach((row) => {
        if (row !== firstRow && isRowEmpty(row)) row.remove();
      });
    }
    if (firstRow) setActiveQueueRow(firstRow);
    updateQueueState();
  };

  /**
   * Fetches results from server cache and updates the Results column.
   * Uses the document ID to fetch per-document results.
   * Supports both web mode (direct endpoint) and Desktop mode (bridge via client_id).
   */
  async function updateRowResultsFromCache(row, { markComplete = false } = {}) {
    const mgr = getMgr?.();
    const documentId = getRowDocumentId(row) || mgr?.currentDocumentId;

    console.log('[Queue] updateRowResultsFromCache - documentId:', documentId);

    let resultsSummary = emptyResultSummary();
    let authoritativeRead = false;

    // Try to fetch from server if we have API access and document ID
    if (apiBase && getAccessToken && documentId) {
      try {
        const token = await getAccessToken();

        // Check if we're in Desktop mode (bridge mode)
        const clientId = getClientId?.();
        const documentPath = getRowDocumentPath(row);
        const isDesktopMode = !!clientId;

        console.log('[Queue] updateRowResultsFromCache mode:', isDesktopMode ? 'Desktop (bridge)' : 'Web');

        // The hosted bridge commonly becomes ready a few seconds after the
        // desktop UI. Until it answers successfully the persisted queue
        // snapshot is not authoritative, so keep the badges blank and retry
        // for a bounded startup window.
        const retryDelays = isDesktopMode ? [0, 500, 1500, 3500, 7000] : [0];
        for (let attempt = 0; attempt < retryDelays.length; attempt++) {
          if (retryDelays[attempt] > 0) {
            await new Promise((resolve) => setTimeout(resolve, retryDelays[attempt]));
          }

          let response;
          try {
            if (isDesktopMode) {
              // Desktop mode: use bridge endpoint. The hosted bridge can be
              // authenticated before the local engine has reconnected during
              // startup, so a short retry prevents the first restored row from
              // being mistaken for an empty cache.
              response = await fetch(`${apiBase}/audit/get_all_cached_results`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  document_id: documentId,
                  document_path: documentPath,
                  client_id: clientId,
                  data_source: 'local'
                })
              });
            } else {
              // Web mode: use direct endpoint
              response = await fetch(`${apiBase}/web/documents/${encodeURIComponent(documentId)}/cached-results`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
            }
          } catch (error) {
            console.warn('[Queue] Cache request failed:', error);
            if (attempt + 1 < retryDelays.length) continue;
            throw error;
          }

          if (!response.ok) {
            console.warn('[Queue] Failed to fetch cached results from server:', response.status);
            if (attempt + 1 < retryDelays.length) continue;
            break;
          }

          const data = await response.json();
          if (data?.status === 'error') {
            console.warn('[Queue] Cache bridge was not ready:', data.error || 'unknown error');
            if (attempt + 1 < retryDelays.length) continue;
            break;
          }

          console.log('[Queue] Fetched cached results from server for', documentId, data);
          resultsSummary = summarizeResults(data?.cached_results || {});
          authoritativeRead = true;
          console.log('[Queue] Results from server cache:', resultsSummary);
          break;
        }
      } catch (err) {
        console.warn('[Queue] Error fetching cached results from server:', err);
      }
    }

    // Fallback: use local mgr.resultsCache if server fetch failed or unavailable
    let hasResults = hasCountableResults(resultsSummary);
    const managerMatchesRow = documentId
      ? !!mgr?.currentDocumentId && String(mgr.currentDocumentId) === String(documentId)
      : !!mgr?.currentDocumentId;

    if (!authoritativeRead && !hasResults && managerMatchesRow) {
      if (mgr?.resultsCache?.calculateSummary) {
        resultsSummary = mgr.resultsCache.calculateSummary();
        hasResults = hasCountableResults(resultsSummary);
        console.log('[Queue] Results from local resultsCache:', resultsSummary);
      }
    } else if (!hasResults && !managerMatchesRow) {
      console.log('[Queue] Skipping local resultsCache fallback for non-active document:', {
        rowDocumentId: documentId,
        activeDocumentId: mgr?.currentDocumentId
      });
    }

    // A failed read is unknown, not an empty cache. Conversely, a successful
    // empty read (for example after deleting the adjacent data folder) is
    // authoritative and must clear any last-viewed localStorage counts.
    if (!authoritativeRead && !hasResults) {
      helpers.resetRowResults?.(row);
      setRowProgress(row, rowHasFile(row) ? 'ready' : 'idle', 0, 0);
      return { authoritative: false, summary: null };
    }

    // Update the row. "Complete" is only claimed when a check actually ran
    // (markComplete) or produced countable results — merely revisiting a
    // document must not label it Complete.
    setRowResults(row, resultsSummary);
    const hasCountable = hasCountableResults(resultsSummary);
    if (markComplete || hasCountable) {
      setRowProgress(row, 'complete', 0, 0);
    } else {
      setRowProgress(row, rowHasFile(row) ? 'ready' : 'idle', 0, 0);
    }
    scheduleQueueHistorySave();
    return { authoritative: authoritativeRead, summary: resultsSummary };
  }

  async function updateRowsResultsFromCache(rows) {
    const pendingRows = (rows || []).filter((row) => getRowDocumentId(row));
    if (!pendingRows.length) return;
    const clientId = getClientId?.();
    if (!clientId) {
      await Promise.all(pendingRows.map((row) => updateRowResultsFromCache(row)));
      return;
    }

    try {
      const token = await getAccessToken();
      const documents = pendingRows.map((row) => ({
        document_id: getRowDocumentId(row),
        document_path: getRowDocumentPath(row),
        data_source: 'local',
        request_key: cacheRequestKey(getRowDocumentId(row), getRowDocumentPath(row)),
      }));
      let data = null;
      for (const delay of [0, 500, 1500, 3500, 7000]) {
        if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
        try {
          const response = await fetch(`${apiBase}/audit/get_cached_result_summaries`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ client_id: clientId, documents }),
          });
          const candidate = await response.json().catch(() => ({}));
          if (response.ok && candidate?.status === 'ok') { data = candidate; break; }
        } catch (error) {
          console.warn('[Queue] Batch cache summary request failed:', error);
        }
      }
      if (!data) return;

      const byRequest = new Map((data.items || []).map((item) => [String(item.request_key || ''), item]));
      for (const row of pendingRows) {
        const requestKey = cacheRequestKey(getRowDocumentId(row), getRowDocumentPath(row));
        const item = byRequest.get(requestKey);
        if (!item || item.status !== 'ok') continue;
        const summary = item.summary || summarizeResults(item.cached_results || {});
        setRowResults(row, summary);
        setRowProgress(row, hasResultCounts(summary) ? 'complete' : 'ready', 0, 0);
      }
      scheduleQueueHistorySave();
    } catch (error) {
      console.warn('[Queue] Could not hydrate restored cache summaries:', error);
    }
  }

  const hasCountableResults = hasResultCounts;

  const getLocalResultsSummaryForRow = (row) => {
    const mgr = getMgr?.();
    const documentId = getRowDocumentId(row) || mgr?.currentDocumentId;
    const managerMatchesRow =
      !documentId ||
      !mgr?.currentDocumentId ||
      String(mgr.currentDocumentId) === String(documentId);

    if (!managerMatchesRow || !mgr?.resultsCache?.calculateSummary) return null;
    return mgr.resultsCache.calculateSummary();
  };

  const refreshRowResults = async (row = getActiveQueueRow()) => {
    if (!row) return;

    const localSummary = getLocalResultsSummaryForRow(row);
    if (hasCountableResults(localSummary)) {
      console.log('[Queue] Refreshing row results from active resultsCache:', localSummary);
      setRowResults(row, localSummary);
      setRowProgress(row, 'complete', 0, 0);
      return;
    }

    await updateRowResultsFromCache(row);
  };

  helpers.refreshActiveRowResults = () => refreshRowResults(getActiveQueueRow());
  helpers.refreshRowResults = refreshRowResults;

  window.addEventListener('fc:document-cache-cleared', (event) => {
    const documentId = String(event?.detail?.documentId || '');
    const documentPath = normalizeHistoryPath(event?.detail?.documentPath || '');
    if (!documentId) return;
    for (const row of getRows()) {
      if (String(getRowDocumentId(row) || '') !== documentId) continue;
      if (documentPath && normalizeHistoryPath(getRowDocumentPath(row)) !== documentPath) continue;
      helpers.resetRowResults?.(row);
      setRowProgress(row, rowHasFile(row) ? 'ready' : 'idle', 0, 0);
    }
  }, { signal });

  /**
   * Loads cached results for a file when it's assigned to a row.
   * Generates document ID from file bytes and fetches cached results from server.
   * Supports both web mode (direct endpoint) and Desktop mode (bridge via client_id).
   *
   * Also settles the card overlay: the row shows 'checking' immediately, then
   * hides as soon as we know the document was already prepared (cached results
   * or an OCR cache exist). If preparation is actually needed, the fc:parse-*
   * events own the overlay from here on.
   */
  const loadCachedResultsForFile = async (row, file) => {
    // Skip if the row already has results (e.g. returning from View Details after a cite-check)
    if (row && rowResultsMap.has(row)) return;

    // prepared=true → document already has OCR/results, hide unconditionally.
    // prepared=false → nothing cached; leave an in-flight 'ocr' overlay alone.
    const settleOverlay = ({ prepared }) => {
      if (!row) return;
      if (prepared || getCardOverlayState(row) !== 'ocr') {
        hideCardOverlay(row);
      }
    };

    if (!row || !file || !apiBase || !getAccessToken) {
      settleOverlay({ prepared: false });
      return;
    }

    // Immediate feedback — before hashing/network — so there is no dead time
    // between adding a file and the card reacting. A fast cache hit never
    // flashes this state thanks to the CSS transition delay on 'is-checking'.
    if (getCardOverlayState(row) !== 'ocr') {
      setCardOverlay(row, 'checking');
    }

    try {
      // Generate document ID from file bytes
      const bytes = await file.arrayBuffer();
      const documentId = await generateDocumentId(bytes);

      if (!documentId) {
        console.log('[Queue] Could not generate document ID for file:', file.name);
        settleOverlay({ prepared: false });
        return;
      }

      // Store document ID for this row
      setRowDocumentId(row, documentId);
      console.log('[Queue] Generated document ID for file:', file.name, '->', documentId);

      // Check if we're in Desktop mode (bridge mode)
      const clientId = getClientId?.();
      const documentPath = file.path || file.__fcPath || getDocumentPath?.();
      const isDesktopMode = !!clientId;

      console.log('[Queue] Mode:', isDesktopMode ? 'Desktop (bridge)' : 'Web', 'clientId:', clientId, 'documentPath:', documentPath);

      let cachedResults = {};
      let hasOcrCache = false;
      const token = await getAccessToken();

      if (isDesktopMode) {
        // Desktop mode: use bridge endpoint
        const response = await fetch(`${apiBase}/audit/get_all_cached_results`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            document_id: documentId,
            document_path: documentPath,
            client_id: clientId,
            data_source: 'local'
          })
        });

        if (!response.ok) {
          console.log('[Queue] No cached results from bridge for document:', documentId, '(status:', response.status, ')');
          settleOverlay({ prepared: false });
          return;
        }

        const data = await response.json();
        console.log('[Queue] Found cached results via bridge for document:', documentId, data);
        cachedResults = data?.cached_results || {};
        hasOcrCache = !!data?.has_ocr_cache;
      } else {
        // Web mode: use direct endpoint
        const response = await fetch(`${apiBase}/web/documents/${encodeURIComponent(documentId)}/cached-results`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.log('[Queue] No cached results for document:', documentId, '(status:', response.status, ')');
          settleOverlay({ prepared: false });
          return;
        }

        const data = await response.json();
        console.log('[Queue] Found cached results for document:', documentId, data);
        cachedResults = data?.cached_results || {};
        hasOcrCache = !!data?.has_ocr_cache;
      }

      const footnoteResults = cachedResults.footnotes || {};
      const inlineResults = cachedResults.inline || {};

      // Check if there are any results
      const hasFootnoteResults = Object.keys(footnoteResults).length > 0;
      const hasInlineResults = Object.keys(inlineResults).length > 0;

      if (!hasFootnoteResults && !hasInlineResults) {
        console.log('[Queue] Cached results empty for document:', documentId, 'has_ocr_cache:', hasOcrCache);
        // No verification results. If an OCR cache exists the document is
        // already prepared → no overlay. Otherwise any in-flight parse keeps
        // its overlay (driven by fc:parse-* events).
        settleOverlay({ prepared: hasOcrCache });
        return;
      }

      const resultsSummary = summarizeResults(cachedResults);

      // Only update if we have results
      if (hasCountableResults(resultsSummary)) {
        console.log('[Queue] Displaying cached results for', file.name, ':', resultsSummary);
        setRowResults(row, resultsSummary);
        setRowProgress(row, 'complete', 0, 0);
        scheduleQueueHistorySave();
      }

      // Cached results exist → the document was already prepared
      settleOverlay({ prepared: true });
    } catch (err) {
      console.warn('[Queue] Error loading cached results for file:', file.name, err);
      settleOverlay({ prepared: false });
    }
  };

  // --- Parse lifecycle events ---------------------------------------------
  // The per-app parse handlers (web file-upload/pdf-parsing, desktop
  // file-selection/pdf-parsing) dispatch fc:parse-started/-progress/
  // -finished/-error; the card overlay follows the actual parse state.
  const resolveParseEventRow = (detail = {}) => {
    const rows = getRows();
    if (detail.documentId) {
      const match = rows.find((r) => String(getRowDocumentId(r) || '') === String(detail.documentId));
      if (match) return match;
    }
    if (detail.fileName) {
      const match = rows.find((r) => (r.querySelector('.queue-filename-text')?.textContent || '') === detail.fileName);
      if (match) return match;
    }
    return getActiveQueueRow();
  };

  window.addEventListener('fc:parse-started', (event) => {
    const detail = event?.detail || {};
    const row = resolveParseEventRow(detail);
    if (!row) return;
    if (detail.documentId) setRowDocumentId(row, detail.documentId);
    // Batch runs report through the status pill instead of the overlay
    if (window.__FC_BATCH_MODE__) {
      hideCardOverlay(row);
      return;
    }
    // Extant OCR → parse will resolve from cache; no overlay needed
    if (detail.ocrCached) {
      hideCardOverlay(row);
      return;
    }
    setCardOverlay(row, 'ocr');
  }, { signal });

  window.addEventListener('fc:parse-progress', (event) => {
    const detail = event?.detail || {};
    const row = resolveParseEventRow(detail);
    if (!row || getCardOverlayState(row) !== 'ocr') return;
    updateCardOverlay(row, { title: detail.title, detail: detail.detail });
  }, { signal });

  window.addEventListener('fc:parse-finished', (event) => {
    const row = resolveParseEventRow(event?.detail || {});
    if (row) hideCardOverlay(row);
  }, { signal });

  window.addEventListener('fc:parse-error', (event) => {
    const detail = event?.detail || {};
    const row = resolveParseEventRow(detail);
    if (!row) return;
    if (window.__FC_BATCH_MODE__ || getCardOverlayState(row) === 'hidden') return;
    setCardOverlay(row, 'error', { detail: detail.message || '' });
  }, { signal });

  // Helper to wait for PDF parsing to complete (pages to be populated)
  // PDF parsing is auto-triggered by file input change handler
  const waitForPdfParsing = (timeoutMs = 120000, { expectedDocumentId = null } = {}) => new Promise((resolve) => {
    // Pages only count when they belong to the expected document — pages left
    // over from a previous row (not yet cleared, or filled in late by that
    // row's still-running parse) must not satisfy this wait.
    const isReady = (m) => (m?.pages?.length || 0) > 0 &&
      (!expectedDocumentId || String(m?.currentDocumentId || '') === String(expectedDocumentId));
    const mgr = getMgr?.();
    console.log('[Queue] waitForPdfParsing called, getMgr exists:', !!getMgr, 'mgr exists:', !!mgr);
    if (isReady(mgr)) {
      console.log('[Queue] Pages already loaded:', mgr.pages.length);
      resolve(true);
      return;
    }

    console.log('[Queue] Waiting for PDF auto-parsing to complete... (timeout:', timeoutMs, 'ms)');
    const startTime = Date.now();
    let logCount = 0;
    let interval = null;
    const finish = (result) => {
      clearInterval(interval);
      window.removeEventListener('fc:parse-error', onParseError);
      resolve(result);
    };
    // Fail fast when the parse itself reports failure (corrupt PDF, engine
    // down) instead of polling for pages until the timeout expires.
    const onParseError = (event) => {
      const detail = event?.detail || {};
      // Ignore late errors from another document's parse when we can tell.
      if (expectedDocumentId && detail.documentId &&
          String(detail.documentId) !== String(expectedDocumentId)) return;
      console.error('[Queue] Parse reported an error while waiting:', detail.message || '(no message)');
      finish(false);
    };
    window.addEventListener('fc:parse-error', onParseError, { signal });
    interval = setInterval(() => {
      const currentMgr = getMgr?.();
      const elapsed = Date.now() - startTime;
      const pagesLen = currentMgr?.pages?.length || 0;

      // Log every 5 seconds to show progress
      if (++logCount % 10 === 0) {
        console.log('[Queue] Still waiting... elapsed:', elapsed, 'ms, pages:', pagesLen, 'mgr exists:', !!currentMgr);
      }

      if (isReady(currentMgr)) {
        console.log('[Queue] PDF parsing complete after', elapsed, 'ms, pages:', pagesLen);
        finish(true);
      } else if (elapsed >= timeoutMs) {
        console.error('[Queue] PDF parsing timed out after', elapsed, 'ms. mgr exists:', !!currentMgr, 'pages:', pagesLen, 'expected doc:', expectedDocumentId, 'actual doc:', currentMgr?.currentDocumentId);
        finish(false);
      }
    }, 500);
  });

  const runQueueRow = async (row) => {
    const file = await loadRowFileForAction(row, 'Add a PDF to the queue before running.');
    if (!row || !file) {
      // An unreadable history row gets an error pill so a batch shows which
      // document was skipped instead of leaving its 'Ready' pill in place.
      if (row && rowHasFile(row)) setRowProgress(row, 'error', 0, 0);
      return;
    }

    console.log('[Queue] ========== Starting verification for:', file.name, '==========');
    lockDetails();
    // Enable batch mode before syncing the file input: the sync triggers the
    // auto-parse, and batch progress is reported via the status pill, not the
    // card overlay.
    window.__FC_BATCH_MODE__ = true;
    // Ensure overlay is dismissed before running
    hideCardOverlay(row);

    // Ensure fileInput is synced to the active row's file before processing
    // This triggers the file input change handler which auto-parses the PDF
    setActiveQueueRow(row);
    updateFileInput(fileInput, file);

    try {
      // Wait for the app's file-change handler to swap the manager over to
      // this row's document. The handler reads and hashes the file before it
      // dispatches the context event, which can take far longer than 5s on
      // large files — and until it fires, the manager still holds the
      // PREVIOUS document. Proceeding on timeout would verify that stale
      // state and attribute its results (and document ID) to this row, so a
      // missing context is a hard per-row failure.
      const expectedDocId = getRowDocumentId(row) || null;
      console.log('[Queue] Waiting for document context...');
      const contextResult = await waitForDocumentContext(file, {
        timeoutMs: 120000,
        expectedDocumentId: expectedDocId
      });
      console.log('[Queue] Document context result:', contextResult);
      if (!contextResult) {
        console.error('[Queue] Document context was not established for', file.name);
        setRowProgress(row, 'error', 0, 0);
        platform.setStatus?.(`Could not load ${file.name}: the document never became active.`);
        return;
      }
      const activeDocId = contextResult.documentId || expectedDocId || null;

      // Log mgr state after context is set
      const mgrAfterContext = getMgr?.();
      console.log('[Queue] mgr state after context:', {
        documentId: mgrAfterContext?.currentDocumentId,
        itemsCount: mgrAfterContext?.items?.length || 0,
        footnotesCount: mgrAfterContext?.footnotesCache?.length || 0,
        pagesCount: mgrAfterContext?.pages?.length || 0
      });

      // Give the auto-parse setTimeout(100ms) time to start
      // The file change handler dispatches context event, then starts parsing in setTimeout
      await new Promise(r => setTimeout(r, 200));

      // Hide any existing verify-all overlay (in case one is visible from previous non-batch verification)
      const existingOverlay = document.getElementById('verify-all-overlay');
      if (existingOverlay) {
        existingOverlay.classList.add('hidden');
        existingOverlay.setAttribute('aria-hidden', 'true');
      }

      // Set up progress callback for status updates
      window.__FC_BATCH_PROGRESS__ = (stage, current, total) => {
        setRowProgress(row, stage, current, total);
      };

      // Set up results callback
      window.__FC_BATCH_RESULTS__ = (results) => {
        setRowResults(row, results);
        setRowProgress(row, 'complete', 0, 0);
        scheduleQueueHistorySave();
      };

      // Set up error callback. rowFailed keeps the results step from
      // overwriting the error pill with 'complete' when verifyAllSmart reports
      // a blocked, cancelled, or failed run through this callback.
      let rowFailed = false;
      window.__FC_BATCH_ERROR__ = (error) => {
        rowFailed = true;
        setRowProgress(row, 'error', 0, 0);
        console.error('[Queue] Batch verification error:', error);
        platform.setStatus?.(`${file.name}: ${error}`);
      };

      // Step 1: Wait for PDF footnotes parsing (auto-triggered by file input change)
      setRowProgress(row, 'parsing', 0, 0);
      // Full OCR of a long document can take several minutes; use the same
      // budget as the OCR overlay failsafe (OVERLAY_FAILSAFE_MS.ocr) instead
      // of erroring at 2 minutes while the parse is still legitimately
      // running. Outright parse failures resolve fast via fc:parse-error.
      const parsed = await waitForPdfParsing(600000, { expectedDocumentId: activeDocId });
      if (!parsed) {
        console.error('[Queue] PDF parsing timed out or failed');
        setRowProgress(row, 'error', 0, 0);
        return;
      }

      // Log mgr state after PDF parsing and capture document ID for this row
      const mgrAfterParsing = getMgr?.();
      const rowDocId = mgrAfterParsing?.currentDocumentId;
      console.log('[Queue] mgr state after PDF parsing:', {
        documentId: rowDocId,
        itemsCount: mgrAfterParsing?.items?.length || 0,
        footnotesCount: mgrAfterParsing?.footnotesCache?.length || 0,
        pagesCount: mgrAfterParsing?.pages?.length || 0,
        resultsCacheSize: mgrAfterParsing?.resultsCache?.size || 0
      });

      // Store document ID for this row so we can fetch its results later
      if (rowDocId) {
        setRowDocumentId(row, rowDocId);
        console.log('[Queue] Stored document ID for row:', rowDocId);
      }

      const inlineToggle = row?.querySelector('.queue-inline-toggle');

      if (inlineToggle?.checked) {
        const hasInlineFootnotes = (mgrAfterParsing?.footnotesCache || [])
          .some((fn) => fn?.is_inline || fn?.inline_citation);

        // Check if inline parse button is disabled (already parsed or stale flag)
        if (btnParseInline?.disabled && hasInlineFootnotes) {
          console.log('[Queue] Inline citations already loaded for this document; skipping inline parse');
          await runActiveDocumentVerification();
        } else {
          if (btnParseInline?.disabled) {
            // Force enable the button - the localStorage flag may be stale
            // The backend will use cache if already parsed, or parse fresh if not
            btnParseInline.disabled = false;
            btnParseInline.title = '';
          }

          // Step 2: Parse inline citations
          setRowProgress(row, 'parsing', 0, 0);
          const inlineParsed = await new Promise((resolve) => {
            const fallbackTimer = setTimeout(() => {
              console.warn('[Queue] Inline parsing timed out after 10 minutes');
              delete window.__FC_QUEUE_INLINE_CALLBACK__;
              setRowProgress(row, 'error', 0, 0);
              resolve(false);
            }, 600000);
            // Inline parsing is async (SSE-based, takes 1-3 minutes)
            // Set up a callback to trigger verification after parsing completes
            window.__FC_QUEUE_INLINE_CALLBACK__ = () => {
              delete window.__FC_QUEUE_INLINE_CALLBACK__;
              // Small delay to ensure UI updates are complete
              setTimeout(() => {
                runActiveDocumentVerification()
                  .then(() => {
                    clearTimeout(fallbackTimer);
                    resolve(true);
                  })
                  .catch((error) => {
                    clearTimeout(fallbackTimer);
                    console.error('[Queue] Verification after inline parsing failed:', error);
                    setRowProgress(row, 'error', 0, 0);
                    resolve(false);
                  });
              }, 100);
            };
            btnParseInline?.click();
          });
          if (!inlineParsed) return;
        }
      } else {
        // No inline parsing needed, verify immediately
        await runActiveDocumentVerification();
      }

      // Step 3: Fetch results from mgr.resultsCache and update Results column
      // (markComplete: a check really ran, even if it found zero citations).
      // A run that reported failure keeps its error pill instead.
      if (!rowFailed) {
        await updateRowResultsFromCache(row, { markComplete: true });
      }

    } catch (error) {
      console.error('[Queue] Verification failed for', file.name, error);
      setRowProgress(row, 'error', 0, 0);
      platform.setStatus?.(`Verification failed for ${file.name}: ${error?.message || error}`);
    } finally {
      // Clean up batch mode
      delete window.__FC_BATCH_MODE__;
      delete window.__FC_BATCH_PROGRESS__;
      delete window.__FC_BATCH_RESULTS__;
      delete window.__FC_BATCH_ERROR__;
      unlockDetails();
    }
  };

  // Queue body click handler (row selection, file/folder buttons, remove)
  queueBody?.addEventListener('click', async (event) => {
    const target = event.target;
    const row = target.closest('.queue-row');
    if (row) setActiveQueueRow(row);

    // While a queue check runs, block actions that would mutate the queue or
    // start a second run against the shared document manager. The buttons are
    // also disabled; this guards non-button paths (e.g. card overlay actions).
    if (detailsLockCount > 0 &&
        target.closest('.queue-check-row, .queue-select-file, .queue-select-folder, .queue-remove-row')) {
      platform.setStatus?.('Queue changes are available after the current check finishes.');
      return;
    }

    if (target.closest('.queue-select-file')) {
      setActiveQueueRow(row);
      if (onSelectFile) {
        const handled = await onSelectFile({ row });
        if (handled) return;
      }
      fileInput?.click();
    }

    if (target.closest('.queue-select-folder')) {
      setActiveQueueRow(row);
      await ensureRowFileLoaded(row);
      // Keep the OCR overlay up while the folder picker is open; it gets a
      // confirmation state in setQueueRowFolder once a folder is chosen.
      if (getCardOverlayState(row) !== 'ocr') hideCardOverlay(row);
      window.dispatchEvent(new CustomEvent('fc:data-source-chosen', { detail: { source: 'folder', row } }));
      if (onSelectFolder) {
        const handled = await onSelectFolder({ row, file: getRowFile(row) });
        if (handled) return;
      }
      folderInput?.click();
    }

    if (target.closest('.queue-remove-row')) {
      if (!queueBody) return;
      const rows = queueBody.querySelectorAll('.queue-row');
      if (rows.length <= 1) {
        row.remove();
        setActiveQueueRow(null);
        updateQueueState();
        return;
      }
      row.remove();
      setActiveQueueRow(queueBody.querySelector('.queue-row'));
      updateQueueState();
    }

    if (target.closest('.queue-show-single')) {
      if (detailsLockCount > 0) {
        platform.setStatus?.('Details are available after the current queue check finishes.');
        return;
      }
      const file = await loadRowFileForAction(row, 'Choose a PDF before opening file view.');
      if (!file) return;
      // If this row's document is already loaded in the manager, open the view
      // directly — re-syncing the file input would re-trigger a parse round
      // trip (and flash the "Parsing…" pill) for a document that is ready.
      const mgrForView = getMgr?.();
      const rowDocId = getRowDocumentId(row);
      const alreadyLoaded = rowDocId && mgrForView?.currentDocumentId &&
        String(mgrForView.currentDocumentId) === String(rowDocId) &&
        (mgrForView.pages?.length || 0) > 0;
      if (alreadyLoaded) {
        onShowSingleView?.(row);
        return;
      }
      // Force file selection to update document context before switching views
      updateFileInput(fileInput, file);
      const viewContext = await waitForDocumentContext(file, {
        timeoutMs: 120000,
        expectedDocumentId: rowDocId || null
      });
      if (!viewContext) {
        setRowProgress(row, 'error', 0, 0);
        platform.setStatus?.(`Could not load ${file.name} for file view.`);
        return;
      }
      let showedWaitingState = false;
      const waitingStateTimer = setTimeout(() => {
        showedWaitingState = true;
        setRowProgress(row, 'parsing', 0, 0);
      }, 1200);
      const parsed = await waitForPdfParsing(120000, {
        expectedDocumentId: viewContext.documentId || rowDocId || null
      });
      clearTimeout(waitingStateTimer);
      if (!parsed) {
        setRowProgress(row, 'error', 0, 0);
        platform.setStatus?.('OCR is still running. Details are available after the document has finished parsing.');
        return;
      }
      if (showedWaitingState) setRowProgress(row, 'ready', 0, 0);
      onShowSingleView?.(row);
    }

    if (target.closest('.queue-generate-pdf-report')) {
      await generateQueuePdfReport(row);
    }

    if (target.closest('.queue-submit-report-job')) {
      await submitQueueReportJob(row);
    }

    if (target.closest('.queue-check-row')) {
      await runQueueRow(row);
    }
  }, { signal });

  queueBody?.addEventListener('change', (event) => {
    if (event.target?.classList?.contains('queue-inline-toggle')) {
      scheduleQueueHistorySave();
    }
  }, { signal });

  // Add row button
  btnAddRow?.addEventListener('click', () => {
    const row = cloneQueueRow();
    if (row && queueBody) {
      queueBody.appendChild(row);
      setActiveQueueRow(row);
      updateQueueState();
    }
  }, { signal });

  // Dropzone click handler
  queueDropzone?.addEventListener('click', async () => {
    if (detailsLockCount > 0) {
      platform.setStatus?.('Adding files is available after the current queue check finishes.');
      return;
    }
    const activeRow = getActiveQueueRow();
    if (activeRow) {
      setActiveQueueRow(activeRow);
    }
    if (onSelectFile) {
      const handled = await onSelectFile({ row: getActiveQueueRow() });
      if (handled) return;
    }
    fileInput?.click();
  }, { signal });

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // No inviting highlight while a check runs — the drop would be rejected.
    setDropzoneDragState(detailsLockCount === 0);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = detailsLockCount > 0 ? 'none' : 'copy';
    }
    setDropzoneDragState(detailsLockCount === 0);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const relatedTarget = event.relatedTarget;
    if (queuePanel && relatedTarget instanceof Node && queuePanel.contains(relatedTarget)) {
      return;
    }
    setDropzoneDragState(false);
  };

  const handleDomDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDropzoneDragState(false);
    const files = getPdfFilesFromDataTransfer(event.dataTransfer);
    await addFilesToQueue(files, 'DOM drop');
  };

  for (const target of [queueDropzone, queuePanel].filter(Boolean)) {
    target.addEventListener('dragenter', handleDragEnter, { signal });
    target.addEventListener('dragover', handleDragOver, { signal });
    target.addEventListener('dragleave', handleDragLeave, { signal });
    target.addEventListener('drop', handleDomDrop, { signal });
  }

  if (isTauriContext()) {
    let unlistenTauriDrop = null;
    const setupTauriDropListener = async () => {
      try {
        const { getCurrentWebview } = await import('@tauri-apps/api/webview');
        const webview = getCurrentWebview();
        unlistenTauriDrop = await webview.onDragDropEvent(async (event) => {
          const payload = event?.payload || {};
          if (payload.type === 'over' || payload.type === 'enter') {
            setDropzoneDragState(true);
            return;
          }
          if (payload.type === 'leave') {
            setDropzoneDragState(false);
            return;
          }
          if (payload.type !== 'drop') return;

          setDropzoneDragState(false);
          try {
            const files = await createFilesFromTauriPaths(payload.paths || []);
            await addFilesToQueue(files, 'Tauri drop');
          } catch (error) {
            console.error('[Queue] Failed to read dropped Tauri files:', error);
            platform.setStatus?.(`Could not read dropped file: ${error.message || error}`);
          }
        });
      } catch (error) {
        console.warn('[Queue] Tauri drag/drop listener unavailable; using DOM drop only.', error);
      }
    };

    setupTauriDropListener();
    signal?.addEventListener('abort', () => {
      try { unlistenTauriDrop?.(); } catch {}
    }, { once: true });
  }

  // File input change
  fileInput?.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    const isQueueSync = fileInput.dataset.fcQueueSync === '1';
    const isQueueAdd = fileInput.dataset.fcQueueAdd === '1';
    if (isQueueSync) {
      delete fileInput.dataset.fcQueueSync;
    }
    if (isQueueAdd) {
      delete fileInput.dataset.fcQueueAdd;
    }

    let row = getActiveQueueRow();
    if (isQueueSync && !isQueueAdd) {
      // If we're syncing from an existing row (e.g., Show File), reuse it.
      if (!row && queueBody) {
        const rows = Array.from(queueBody.querySelectorAll('.queue-row'));
        row = rows.length === 1 ? rows[0] : null;
      }
    }

    const canReuse = isQueueSync && !isQueueAdd && row;
    if (!canReuse) {
      const newRow = cloneQueueRow();
      if (newRow && queueBody) {
        queueBody.appendChild(newRow);
        row = newRow;
      }
    }
    if (row) {
      setActiveQueueRow(row);
      setQueueRowFile(row, file);
      // Load cached results in background
      loadCachedResultsForFile(row, file);
      if (onQueueFileSelected) {
        await onQueueFileSelected({ row, file, isQueueAdd, isQueueSync });
      }
    }
    updateQueueState();
  }, { signal });

  // Folder input change
  folderInput?.addEventListener('change', () => {
    const file = folderInput.files?.[0];
    if (!file) return;
    const folderName = file.webkitRelativePath?.split('/')[0] || file.name;
    const files = Array.from(folderInput.files || []);
    const row = getActiveQueueRow();
    setQueueRowFolder(row, folderName);
    onFolderSelected?.({ row, file: getRowFile(row), folderName, files });
    updateQueueState();
  }, { signal });

  // Queue Go button
  btnQueueGo?.addEventListener('click', async () => {
    if (detailsLockCount > 0) {
      platform.setStatus?.('A queue check is already running.');
      return;
    }
    const row = getActiveQueueRow();
    await runQueueRow(row);
  }, { signal });

  btnQueueVerifyAll?.addEventListener('click', async () => {
    if (detailsLockCount > 0) {
      platform.setStatus?.('A queue check is already running.');
      return;
    }
    const rows = getRowsWithFiles();
    if (!rows.length) {
      platform.setStatus?.('Add PDFs to the queue before running.');
      return;
    }
    btnQueueVerifyAll.disabled = true;
    lockDetails();
    try {
      for (const row of rows) {
        setActiveQueueRow(row);
        try {
          await runQueueRow(row);
        } catch (error) {
          // A failed document must not abort the rest of the batch.
          console.error('[Queue] Verify All: document failed, continuing:', error);
          setRowProgress(row, 'error', 0, 0);
        }
      }
    } finally {
      unlockDetails();
      btnQueueVerifyAll.disabled = false;
    }
  }, { signal });

  updateQueueState();
  restoreSavedQueueHistory().catch((error) => {
    console.warn('[Queue] Failed to restore queue history:', error);
  });
}

/**
 * Initializes the complete queue UI system
 * @param {Object} config - Configuration object
 * @param {HTMLElement} config.queueBody - The queue table body element
 * @param {HTMLElement} config.queueDropzone - The dropzone element
 * @param {HTMLElement} config.btnAddRow - Add row button
 * @param {HTMLElement} config.btnQueueGo - Go/run button
 * @param {HTMLInputElement} config.fileInput - File input element
 * @param {HTMLInputElement} config.folderInput - Folder input element
 * @param {HTMLElement} config.pickedNameEl - Element to display selected file name
 * @param {AbortSignal} config.signal - AbortController signal for cleanup
 * @param {Object} config.platform - Platform adapter with setStatus method
 * @param {HTMLElement} config.btnParse - Parse PDF button (optional)
 * @param {HTMLElement} config.btnParseInline - Parse inline button (optional)
 * @param {HTMLElement} config.btnVerifyAll - Verify all / Audit all button
 * @param {HTMLElement} config.btnGeneratePdfReport - Generate PDF report button (optional)
 * @param {HTMLElement} config.btnQueueVerifyAll - Verify all documents button (optional)
 * @param {Function} config.getMgr - Function returning the FootnotesManager instance
 * @param {Function} config.onShowSingleView - Callback to open single-file view
 * @param {Function} config.onSelectFile - Optional file picker handler for queue rows
 * @param {Function} config.onSelectFolder - Optional folder picker handler for queue rows
 * @param {Function} config.onFolderSelected - Optional hook after folder input selection
 * @param {Function} config.onSubmitReportJob - Optional async report-job submit handler
 * @param {string} config.apiBase - API base URL for fetching cached results
 * @param {Function} config.getAccessToken - Function to get access token for API calls
 * @param {Function} config.getClientId - Optional function to get client ID for bridge mode (Desktop App)
 * @param {Function} config.getDocumentPath - Optional function to get document path (Desktop App)
 * @param {Function} config.getDocumentId - Optional function to get document ID (Desktop App)
 * @returns {Object} Queue helpers for external use
 */
export function initializeQueueUI({
  queueBody,
  queueDropzone,
  btnAddRow,
  btnQueueGo,
  fileInput,
  folderInput,
  pickedNameEl,
  signal,
  platform,
  btnParse,
  btnParseInline,
  btnVerifyAll,
  btnGeneratePdfReport,
  btnQueueVerifyAll,
  getMgr,
  onShowSingleView,
  onSelectFile,
  onSelectFolder,
  onFolderSelected,
  onQueueFileSelected,
  onSubmitReportJob,
  apiBase,
  getAccessToken,
  getClientId,
  getDocumentPath,
  getDocumentId
}) {
  const baseQueueRow = queueBody?.querySelector('.queue-row');
  const templateRow = document.getElementById('queue-row-template')?.content?.querySelector('.queue-row') || null;

  const helpers = createQueueRowHelpers({
    queueBody,
    baseQueueRow,
    templateRow,
    pickedNameEl,
    fileInput
  });

  setupQueueEventListeners({
    queueBody,
    queueDropzone,
    btnAddRow,
    btnQueueGo,
    fileInput,
    folderInput,
    helpers,
    signal,
    platform,
    btnParse,
    btnParseInline,
    btnVerifyAll,
    btnGeneratePdfReport,
    btnQueueVerifyAll,
    getMgr,
    onShowSingleView,
    onSelectFile,
    onSelectFolder,
    onFolderSelected,
    apiBase,
    getAccessToken,
    onQueueFileSelected,
    onSubmitReportJob,
    getClientId,
    getDocumentPath,
    getDocumentId
  });

  return helpers;
}
